import React, { FC } from 'react';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { analyticsService } from '@web/services/singletons';
import { createUseStyles } from 'react-jss';
import { DATE_FORMAT } from '@web/config/constants';
import { getBadgeVariantTechline, getDisplayDate } from '@web/utils';
import { Link } from 'react-router-dom';
import { StyleSheet } from 'react-native';
import { TableCell, TableHeader, TableRow } from '@web/components/table';
import { TextBadge, Typography, Button } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const useStyles = createUseStyles({
    tableCell: {
        padding: '16px',
    },
    link: {
        fontSize: 15,
        fontWeight: 400,
        '&:hover': {
            textDecoration: 'underline!important',
        },
    },
    tooltipList: {
        listStyleType: 'none',
        padding: '4px 4px',
        margin: 0,
        textAlign: 'left',
    },
    tooltipListItem: {
        padding: '4px 8px',
        textAlign: 'left',
    },
});

const styles = StyleSheet.create({
    tableHeader: {
        fontWeight: '600',
    },
    emptyStateDescriptionTitle: {
        display: 'flex',
        fontSize: 21,
        fontWeight: '600',
        justifyContent: 'center',
        lineHeight: 32,
    },
    emptyStateDescriptionBody: {
        display: 'flex',
        fontSize: 15,
        fontWeight: '400',
        justifyContent: 'center',
        lineHeight: 24,
    },
    emptyStateIcon: {
        backgroundColor: colors.grayFive,
        borderRadius: 50,
    },
});

export const EmptyStateDescription: FC = () => {
    return (
        <>
            <Typography color="textDarkSecondary" style={styles.emptyStateDescriptionTitle}>
                No Matching Records Found!
            </Typography>
        </>
    );
};

const HEADER_TITLES = [
    { title: 'Case No.', width: '20%' },
    { title: 'VIN', width: '15%' },
    { title: 'Status', width: '10%' },
    { title: 'Worksheets', width: '15%' },
    { title: 'Created by', width: '15%' },
    { title: 'RO', width: '10%' },
    { title: 'Last Update', width: '15%' },
];

export const TechlineTableHeader: FC = () => {
    return (
        <TableRow>
            {HEADER_TITLES.map((header) => (
                <TableHeader key={header.title} width={header.width}>
                    <Typography style={styles.tableHeader} numberOfLines={1} ellipsizeMode="tail">
                        {header.title}
                    </Typography>
                </TableHeader>
            ))}
        </TableRow>
    );
};

export interface TechlineTableRowProps {
    rowData: any;
}

export const TechlineTableRow: FC<TechlineTableRowProps> = ({ rowData }) => {
    const { caseNumber, vin, techlineCaseStatus, fullName, repairOrderNumber, modifiedOn, worksheets = [''] } = rowData;
    const classes = useStyles();
    const navigateToCaseDetails = () => {
        if (caseNumber) {
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_OPENS_EXISTING_CASE);
        }
    };

    return (
        <TableRow key={caseNumber}>
            <TableCell className={classes.tableCell}>
                <Typography numberOfLines={1} variant="label" ellipsizeMode="tail">
                    <Link
                        className={classes.link}
                        to={`/techline/${caseNumber}`}
                        target="_blank"
                        onClick={navigateToCaseDetails}
                    >
                        {caseNumber}
                    </Link>
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography numberOfLines={1} ellipsizeMode="tail">
                    {vin}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <TextBadge
                    typographyProps={{ ellipsizeMode: 'tail', numberOfLines: 1 }}
                    variant={getBadgeVariantTechline(techlineCaseStatus)}
                >
                    {techlineCaseStatus}
                </TextBadge>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography numberOfLines={1} ellipsizeMode="tail">
                    {worksheets.length > 1 ? (
                        <>
                            <Typography>{`${worksheets[0]}..,  `}</Typography>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id="refresh-tool-tip">
                                        <ul className={classes.tooltipList}>
                                            {worksheets.map((worksheet: any, index: number) => (
                                                <li key={index} className={classes.tooltipListItem}>
                                                    <Typography color="white" variant="caption4">
                                                        {worksheet}
                                                    </Typography>
                                                </li>
                                            ))}
                                        </ul>
                                    </Tooltip>
                                }
                            >
                                <Typography
                                    color="blueOne"
                                    variant="caption4"
                                >{`+${worksheets.length - 1}`}</Typography>
                            </OverlayTrigger>
                        </>
                    ) : (
                        worksheets[0]
                    )}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography numberOfLines={1} ellipsizeMode="tail">
                    {fullName}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography numberOfLines={1} ellipsizeMode="tail">
                    {repairOrderNumber}
                </Typography>
            </TableCell>
            <TableCell className={classes.tableCell}>
                <Typography numberOfLines={1} ellipsizeMode="tail">
                    {getDisplayDate(modifiedOn, DATE_FORMAT.MMM_D_YEAR)}
                </Typography>
            </TableCell>
        </TableRow>
    );
};
